import { useCallback, useContext } from 'react';
import { useRouter } from '@tanstack/react-router';

import { AppContext } from '@/app.context';
import { useGoBack } from '@shared/lib/hooks';
import { getPreviousHistoryEntrySearch } from '@shared/lib/history-entry-tracker';

/**
 * The issue reader's "back": a real history pop (`useGoBack`), wrapped in a
 * view transition so the cover-morph that brought the reader in plays in
 * reverse on the way out.
 *
 * `router.navigate()`/`<Link>` can't do a real pop -- `commitLocation` only
 * ever issues a `PUSH` or `REPLACE` -- so this owns the transition directly
 * rather than priming `router.shouldViewTransition` and hoping it survives
 * the async popstate gap (confirmed live not to). `onResolved` fires the
 * instant the pop's commit has settled, which is when the transition's
 * "after" snapshot should be taken.
 *
 * **Skipped when the entry being returned to has a `?dialog=` search
 * param** (`basic`'s home, reopening its Favorites/Downloads drawer --
 * `templates/basic/home/home.schema.ts`). That page comes back with a
 * drawer covering it, so there's no cover to morph into, and animating a
 * snapshot of the drawer-less reader into it just looks broken. The
 * previous entry is read from `@shared/lib/history-entry-tracker`, since
 * the History API itself can't be asked what's behind the current entry.
 * An issue opened any other way (e.g. a home-screen cover) still gets the
 * transition.
 *
 * Previously copy-pasted into four back buttons (the responsive topbar,
 * `IssueCover`, and both replica views) -- now each calls this.
 */
export function useReaderGoBack(): () => void {
	const router = useRouter();
	const goBack = useGoBack();
	const { prefersReducedMotion } = useContext(AppContext);

	return useCallback(() => {
		const previousSearch = getPreviousHistoryEntrySearch(
			router.state.location,
		);
		if (
			prefersReducedMotion ||
			typeof document.startViewTransition !== 'function' ||
			!!previousSearch?.dialog
		) {
			goBack();
			return;
		}

		document.startViewTransition(
			() =>
				new Promise<void>((resolve) => {
					const unsubscribe = router.subscribe('onResolved', () => {
						unsubscribe();
						resolve();
					});
					goBack();
				}),
		);
	}, [router, goBack, prefersReducedMotion]);
}
